import { format } from "date-fns";
import { useContext } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { GlobalContext } from "../../context";
import LeaderboardCard from "./LeaderboardCard";

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 text-center">
      <p className="text-xl font-bold" style={{ color }}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

export default function ActivitySidebar({ gamification, gamificationLoading }) {
  const { scheduleState, userLocation } = useContext(GlobalContext);
  const schedule = scheduleState?.data || [];
  const isNG = userLocation?.data === "NG";

  const sessionDates = schedule
    .map((s) => {
      const raw = isNG ? s.time?.wat?.date : s.time?.est?.date;
      return raw ? new Date(raw) : null;
    })
    .filter(Boolean);

  const upcoming = schedule.filter((s) => s.status !== "ended").slice(0, 4);

  const totalSessions     = schedule.length;
  const completedSessions = schedule.filter(
    (s) => s.recording_link || s.status === "recorded" || s.status === "ended"
  ).length;

  const now = new Date();
  const sessionDaysThisMonth = new Set(
    sessionDates
      .filter((d) => d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear())
      .map((d) => d.getDate())
  ).size;

  const tileClassName = ({ date, view }) => {
    if (view !== "month") return null;
    const hasSession = sessionDates.some(
      (d) =>
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
    );
    return hasSession ? "session-tile" : null;
  };

  const streak       = gamification?.streak?.current ?? 0;
  const longestStreak = gamification?.streak?.longest ?? 0;
  const points       = gamification?.points ?? 0;
  const leaderboard  = gamification?.leaderboard?.entries ?? [];

  return (
    <aside className="flex flex-col gap-4">
      {/* Calendar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <h3 className="font-semibold text-gray-800 text-sm mb-3">Schedule Calendar</h3>
        <style>{`
          .session-tile { background: rgba(208,84,36,0.15) !important; color: #d15424 !important; font-weight: 700; border-radius: 6px; }
          .react-calendar { border: none !important; width: 100% !important; font-size: 13px; }
          .react-calendar__tile--active { background: #d15424 !important; border-radius: 6px !important; }
          .react-calendar__navigation button:hover, .react-calendar__tile:hover { background: #fef0eb !important; border-radius: 6px !important; }
        `}</style>
        <Calendar tileClassName={tileClassName} />
      </div>

      {/* Session stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Sessions"  value={totalSessions}      color="#d15424" />
        <StatCard label="Completed" value={completedSessions}  color="#10b981" />
        <StatCard label="This Month" value={sessionDaysThisMonth} color="#294a93" />
      </div>

      {/* Streak + Points */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-[#d15424] to-[#ff8c5a] rounded-2xl p-4 text-white">
          <p className="text-xs opacity-80 mb-0.5">🔥 Streak</p>
          {gamificationLoading ? (
            <div className="h-8 bg-white/20 rounded animate-pulse mt-1" />
          ) : (
            <>
              <p className="text-3xl font-bold leading-tight">
                {streak}
                <span className="text-sm font-normal opacity-90 ml-1">days</span>
              </p>
              <p className="text-xs opacity-70 mt-1">Best: {longestStreak}d</p>
            </>
          )}
        </div>

        <div className="bg-gradient-to-br from-[#294a93] to-[#3b60c4] rounded-2xl p-4 text-white">
          <p className="text-xs opacity-80 mb-0.5">⭐ Points</p>
          {gamificationLoading ? (
            <div className="h-8 bg-white/20 rounded animate-pulse mt-1" />
          ) : (
            <>
              <p className="text-3xl font-bold leading-tight">{points}</p>
              <p className="text-xs opacity-70 mt-1">Total earned</p>
            </>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <LeaderboardCard entries={leaderboard} loading={gamificationLoading} />

      {/* Upcoming classes */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <h3 className="font-semibold text-gray-800 text-sm mb-3">Upcoming Classes</h3>
        {upcoming.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">No upcoming sessions</p>
        ) : (
          <div className="space-y-3">
            {upcoming.map((session, i) => {
              const rawDate = isNG ? session.time?.wat?.date : session.time?.est?.date;
              const isLive  = session.status === "live";
              return (
                <div key={i} className="flex items-start gap-3">
                  {session.courses?.[0]?.image_url ? (
                    <img
                      src={session.courses[0].image_url}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center text-lg">
                      📖
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">
                      {session.courses?.[0]?.title}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{session.topic}</p>
                    {rawDate && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {format(new Date(rawDate), "MMM d, h:mm a")}
                      </p>
                    )}
                  </div>
                  {isLive && (
                    <span className="flex-shrink-0 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">
                      LIVE
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </aside>
  );
}
